import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, AlertDescription } from '@klaro7/ui';

import { requestPasswordReset } from '@/features/auth/services/auth.service';

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      await requestPasswordReset(email);
      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Richiesta fallita. Riprova.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="relative flex min-h-screen items-center justify-center px-4"
      style={{
        background: 'linear-gradient(160deg, #231F55 0%, #1a1740 40%, #126f7d 100%)',
      }}
    >
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background: 'radial-gradient(ellipse at 30% 20%, rgba(24,153,170,0.15) 0%, transparent 60%), radial-gradient(ellipse at 70% 80%, rgba(118,185,185,0.08) 0%, transparent 50%)',
        }}
      />

      <div className="relative z-10 w-full max-w-md">
        <div className="mb-6">
          <Link
            to="/login"
            className="inline-flex items-center gap-1.5 text-sm text-white/50 transition-colors hover:text-white/80"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
              <path fillRule="evenodd" d="M17 10a.75.75 0 0 1-.75.75H5.612l4.158 3.96a.75.75 0 1 1-1.04 1.08l-5.5-5.25a.75.75 0 0 1 0-1.08l5.5-5.25a.75.75 0 1 1 1.04 1.08L5.612 9.25H16.25A.75.75 0 0 1 17 10Z" clipRule="evenodd" />
            </svg>
            Torna al login
          </Link>
        </div>

        <div className="mb-8 flex justify-center">
          <img src="/images/logo-clean.png" alt="Klaro7" className="h-14 drop-shadow-lg" />
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/[0.06] p-8 shadow-2xl backdrop-blur-xl sm:p-10">
          {submitted ? (
            <div className="text-center">
              <h1 className="text-2xl font-semibold tracking-tight text-white">Controlla la tua email</h1>
              <p className="mt-3 text-sm text-white/60">
                Se l'indirizzo è registrato, ti abbiamo inviato un link per reimpostare la password.
                Il link è valido per 1 ora.
              </p>
              <Link
                to="/login"
                className="mt-6 inline-block text-sm font-medium text-brand-teal transition-colors hover:text-brand-cyan hover:underline"
              >
                Torna al login
              </Link>
            </div>
          ) : (
            <>
              <div className="mb-6 text-center">
                <h1 className="text-2xl font-semibold tracking-tight text-white">Password dimenticata?</h1>
                <p className="mt-1.5 text-sm text-white/60">
                  Inserisci la tua email e ti invieremo un link per reimpostarla
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                {error && (
                  <Alert variant="error">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <div className="space-y-1.5">
                  <label htmlFor="forgot-email" className="block text-sm font-medium text-white/70">
                    Email
                  </label>
                  <input
                    id="forgot-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="nome@email.com"
                    required
                    autoComplete="email"
                    className="flex h-11 w-full rounded-lg border border-white/15 bg-white/[0.06] px-3.5 py-2 text-sm text-white placeholder:text-white/30 transition-colors duration-200 focus:border-brand-teal/50 focus:bg-white/[0.1] focus:outline-none focus:ring-2 focus:ring-brand-teal/20"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="mt-2 flex h-11 w-full items-center justify-center gap-2 rounded-lg font-medium text-white shadow-md transition-all duration-300 hover:shadow-lg hover:shadow-brand-teal/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-teal focus-visible:ring-offset-2 focus-visible:ring-offset-[#231F55] active:scale-[0.98] disabled:pointer-events-none disabled:opacity-60"
                  style={{
                    background: 'linear-gradient(135deg, #1899AA 0%, #126f7d 100%)',
                  }}
                >
                  {isLoading ? (
                    <>
                      <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      <span className="text-sm">Invio in corso...</span>
                    </>
                  ) : (
                    <span className="text-sm">Invia link di reset</span>
                  )}
                </button>
              </form>

              <p className="mt-6 text-center text-sm text-white/40">
                Ti sei ricordato la password?{' '}
                <Link to="/login" className="font-medium text-brand-teal transition-colors hover:text-brand-cyan hover:underline">
                  Accedi
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
